import React, { useState, useEffect } from 'react';
import {
  Users2,
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  Info,
  CheckCircle,
  X,
  Loader2,
  UserPlus,
} from 'lucide-react';
import { GroupActivity } from '@/types/groupActivity';
import { formatDateForDisplay } from '@/lib/utils/date';
import { fetchActivityImage } from '@/lib/fetchActivityImage';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';

import { getVenueCoordinates } from '@/lib/coordinates';
import AddToCalendarButton from './buttons/AddToCalendarButton';
import LocationActions from '../facilities/LocationActions';

interface GroupActivityCardProps {
  activity: GroupActivity;
  onJoin: (activityId: string) => Promise<void>;
  onLeave: (activityId: string) => Promise<void>;
  onCancel: (activityId: string) => Promise<void>;
  onInvite?: (activity: GroupActivity) => void;
  isJoining?: boolean;
  isLeaving?: boolean;
  isCancelling?: boolean;
}

// helper to display H:MM in 24h format
const formatTime = (timeStr: string) => {
  const [hour, minute] = timeStr.split(':');
  return `${parseInt(hour, 10)}:${minute}`;
};

const GroupActivityCard: React.FC<GroupActivityCardProps> = ({
  activity,
  onJoin,
  onLeave,
  onCancel,
  onInvite,
  isJoining = false,
  isLeaving = false,
  isCancelling = false,
}) => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [groupImage, setGroupImage] = useState<string>('');
  const [descExpanded, setDescExpanded] = useState(false);
  const isLongDesc = (activity.description || '').length > 100;

  useEffect(() => {
    (async () => {
      try {
        const url = await fetchActivityImage(activity.sport);
        setGroupImage(url);
      } catch {
        // fallback inside fetchActivityImage
      }
    })();
  }, [activity.sport]);

  const isCreator     = activity.isCreator;
  const isParticipant = activity.userStatus === 'accepted';
  const isFull        = activity.currentParticipants >= activity.maxParticipants;
  const isPast        = new Date(`${activity.groupDate}T${activity.groupTime}`) < new Date();
  const isCancelled   = activity.status === 'cancelled';
  const isCompleted   = activity.status === 'completed';

  // build the Add-to-Calendar event (default duration = 1h)
  const startDate = new Date(`${activity.groupDate}T${activity.groupTime}`);
  const endDate   = new Date(startDate.getTime() + 60 * 60 * 1000);
  const calendarEvent = {
    title:       activity.title,
    description: activity.description || '',
    location:    [activity.venueName, activity.venueCity].filter(Boolean).join(', '),
    startTime:   startDate.toISOString(),
    endTime:     endDate.toISOString(),
  };

  // get coords for navigation
  const coordinates = getVenueCoordinates(activity.venueCity || '');

  const sportEmoji = activity.sport.split(' ')[0];
  const sportName  = activity.sport.split(' ').slice(1).join(' ');
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div
      className={cn(
        'rounded-xl overflow-hidden border transition-all duration-300 cursor-pointer group',
        isDarkMode
          ? 'bg-dark-200 border-dark-300 hover:border-gold-400'
          : 'bg-white border-gray-200 hover:border-gold-600',
        (isCancelled || isCompleted) && 'opacity-75'
      )}
    >
      {(isCancelled || isCompleted) && (
        <div
          className={cn(
            'px-4 py-2 text-center text-sm font-medium',
            isCancelled
              ? 'bg-red-500/20 text-red-500'
              : 'bg-green-500/20 text-green-500'
          )}
        >
          {isCancelled ? 'Cancelled' : 'Completed'}
        </div>
      )}

      {/* Hero image + avatars + participant count */}
      <div className="relative h-48 sm:h-56">
        <img
          src={groupImage}
          alt={activity.title}
          className="w-full h-full object-cover brightness-75"
        />
        <div
          className={cn(
            'absolute inset-0 transition-all duration-300',
            isDarkMode
              ? 'bg-gradient-to-t from-dark-200 via-dark-200/40 to-transparent'
              : 'bg-gradient-to-t from-white via-white/10 to-transparent'
          )}
        />
        {/* Avatars on left */}
        <div className="absolute bottom-0 left-4 translate-y-1/2 flex -space-x-3 z-10">
          {activity.participants?.slice(0, 5).map(p => (
            <img
              key={p.id}
              src={p.userAvatar || '/images/default-avatar.png'}
              alt={p.userName}
              title={p.userName}
              className="w-12 h-12 rounded-full border-2 border-white cursor-pointer"
              onClick={e => {
                stop(e);
                navigate(`/dashboard/members/${p.userId}`);
              }}
            />
          ))}
          {activity.participants && activity.participants.length > 5 && (
            <div className="w-12 h-12 rounded-full bg-gray-200 text-xs flex items-center justify-center border-2 border-white">
              +{activity.participants.length - 5}
            </div>
          )}
        </div>
        {/* Participant count on right */}
        <div className="absolute bottom-0 right-4 translate-y-1/2 bg-black bg-opacity-60 px-2 py-1 rounded-full !text-white text-s z-10">
          {activity.currentParticipants}/{activity.maxParticipants}
        </div>
      </div>

      {/* Main content */}
      <div className="pt-10 p-4">
        {/* Title & Sport */}
        <div className="mb-4">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full flex items-center justify-center mr-3 bg-dark-300 text-2xl">
              {sportEmoji}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">{activity.title}</h3>
              <p className="text-dark-800 text-sm">{sportName}</p>
            </div>
          </div>
        </div>

        <div className="space-y-2 mb-4 text-dark-800">
          {/* Date + Time row */}
          <div className="sm:flex sm:items-center sm:justify-between">
            <div className="flex items-center">
              <span className="text-gold-400 font-medium whitespace-nowrap mr-2">When?</span>
              <span>
                {formatDateForDisplay(activity.groupDate)} @ {formatTime(activity.groupTime)}
              </span>
            </div>
          </div>

          {/* Address row */}
          {activity.venueCity && (
            <div className="sm:flex sm:items-center sm:justify-between mt-2">
              <div className="flex items-center">
                <span className="text-gold-400 font-medium whitespace-nowrap mr-2">Where?</span>
                <span>
                  {[activity.venueName, activity.venueCity].filter(Boolean).join(', ')}
                </span>
              </div>
            </div>
          )}

          {/* Description */}
          {activity.description && (
            <div className="mt-2">
              <div className="flex items-start">
                <Info className="h-4 w-4 mr-2 text-gold-400 mt-1 flex-shrink-0" />
                <p className={`text-sm ${!descExpanded ? 'line-clamp-2' : ''}`}>
                  {activity.description}
                </p>
              </div>
              {isLongDesc && (
                <button
                  onClick={() => setDescExpanded(!descExpanded)}
                  className="mt-1 text-gold-400 text-sm font-medium"
                >
                  {descExpanded ? 'Show less' : 'Read more'}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {!isPast && !isCancelled && !isCompleted && (
              <>
                {isCreator ? (
                  <Button
                    onClick={e => {
                      stop(e);
                      onCancel(activity.id);
                    }}
                    variant="outline"
                    size="sm"
                    className="border-red-500/30 text-red-500 hover:bg-red-500/10"
                    disabled={isCancelling}
                  >
                    {isCancelling ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Cancelling…
                      </>
                    ) : (
                      <>
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </>
                    )}
                  </Button>
                ) : isParticipant ? (
                  <Button
                    onClick={e => {
                      stop(e);
                      onLeave(activity.id);
                    }}
                    variant="outline"
                    size="sm"
                    className="border-dark-400 text-white hover:bg-dark-400"
                    disabled={isLeaving}
                  >
                    {isLeaving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Leaving…
                      </>
                    ) : (
                      <>
                        <X className="h-4 w-4 mr-2" />
                        Leave
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={e => {
                      stop(e);
                      onJoin(activity.id);
                    }}
                    size="sm"
                    className="bg-gold-400 hover:bg-gold-500 text-dark-50"
                    disabled={isJoining || isFull}
                  >
                    {isJoining ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Joining…
                      </>
                    ) : isFull ? (
                      <>
                        <Users2 className="h-4 w-4 mr-2" />
                        Full
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Join
                      </>
                    )}
                  </Button>
                )}

                {onInvite && (isCreator || isParticipant) && (
                  <Button
                    onClick={e => {
                      stop(e);
                      onInvite(activity);
                    }}
                    variant="outline"
                    size="sm"
                    className="border-dark-400 text-white hover:bg-dark-400"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Invite
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupActivityCard;
